import { MappingStatusFilter } from '@/components/Grid/CellRenderers'
import type { components } from '@/types/api/v1'
import { ColDef } from 'ag-grid-community'
import { InferResponse } from './typedClient'

export type CrossroadsEntityType =
  | 'Terminal'
  | 'Product'
  | 'Site'
  | 'Tank'
  | 'Company'
  | 'Tenant'

export const ENTITY_GRID_ENDPOINT_MAP: Record<CrossroadsEntityType, string> = {
  Terminal: '/crossroads/entity/terminals/grid',
  Site: '/crossroads/entity/sites/grid',
  Product: '/crossroads/entity/products/grid',
  Tank: '/crossroads/entity/tanks/grid',
  Company: '/crossroads/network/companies/grid',
  Tenant: '/meta/tenant/grid',
}

/**
 * Normalizes backend goid_type values to CrossroadsEntityType.
 */
export function normalizeGoidType(goidType: string): CrossroadsEntityType {
  const normalizedMap: Record<string, CrossroadsEntityType> = {
    // Direct mappings (PascalCase)
    Terminal: 'Terminal',
    Product: 'Product',
    Site: 'Site',
    Tank: 'Tank',
    Company: 'Company',
    Tenant: 'Tenant',
    // Lowercase variants
    terminal: 'Terminal',
    product: 'Product',
    site: 'Site',
    tank: 'Tank',
    company: 'Company',
    tenant: 'Tenant',
  }
  return normalizedMap[goidType] || 'Company'
}

// Master Data Entity Types
export interface CrossroadsEntity {
  _id?: string
  goid: string
  name: string
  is_active: boolean
}

export type CrossroadsMappingType =
  // | 'location'
  'site' | 'product' | 'counterparty' | 'terminal' | 'tank'

export const MAPPING_ENTITY_MAP: Record<
  CrossroadsMappingType,
  CrossroadsEntityType
> = {
  // location: 'Site',
  product: 'Product',
  site: 'Site',
  counterparty: 'Company',
  terminal: 'Terminal',
  tank: 'Tank',
}

export interface CoreCrossroadsMapping {
  type: CrossroadsMappingType
  goids: string[]
  display_name: string
  source_id: string
  matching_info: object
  extra_data: object
  status: MappingStatusFilter
}

export interface CrossroadsMapping extends CoreCrossroadsMapping {
  source_system: string
  children: Record<string, CoreCrossroadsMapping>
  found_in_latest_sync?: boolean
  _id?: string
  tenant?: string // This is only included from certain endpoints that return data for multiple tenants
}

export interface GridPayload<TData> {
  column_defs: ColDef<TData>[]
  rows: TData[]
  num_results: number
  num_pages: number
}

export type User = InferResponse<'/meta/user/by_id'>

// Crossroads Network Types
export interface Node {
  _id: string
  company_id: string
  system_id: string
  is_phantom: boolean
  is_active?: boolean
}

export interface Company {
  id: string
  name: string
  description?: string
  is_gravitate_customer: boolean
  goid?: string
  is_active?: boolean
  record_owner?: string
  rita_tenant?: string
  ein?: string
  is_retailer?: boolean
  is_wholesaler?: boolean
  is_carrier?: boolean
  is_supplier?: boolean
  is_dealer?: boolean
  _id?: string
}

export interface System {
  id: string
  name: string
  type?: string
  description?: string
  is_active?: boolean
  _id?: string
}

/** Crossroads system types (matches the backend SupportedSystemTypes enum). */
export type SupportedSystemType = components['schemas']['SupportedSystemTypes']

/** API key scopes (matches the backend Scope enum). */
export type Scope = components['schemas']['Scope']

/**
 * Canonical system-type names. Prefer these over string literals so a rename in
 * the backend SupportedSystemTypes enum surfaces here as a type error.
 */
export const SystemType = {
  SND: 'Gravitate Supply & Dispatch',
  EXTERNAL: 'External System',
  KAG_EDI: 'KAG EDI',
} as const satisfies Record<string, SupportedSystemType>
export interface AuditAction {
  _id?: string
  id?: string
  action_id: string
  action_type: string
  entity_type: string
  entity_id: string
  performed_by: string
  status: 'completed' | 'failed' | 'pending' | 'in_progress'
  timestamp: string
  description?: string
  error_message?: string
  metadata?: Record<string, unknown>
}
